import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  notFound,
  useParams,
  useRouter,
  useStableCallback,
} from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useState } from "react"

import type { ExtendedProject } from "@repo/supabase/database-types"
import { Button } from "@repo/ui/components/ui/button"
import { If } from "@repo/ui/components/ui/if"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { getExtendedClientQueryOptions } from "~/modules/clients/clients.queries"
import { CreateProjectModal } from "~/modules/projects/components/create-project-modal"
import { ProjectsTable } from "~/modules/projects/components/projects-table"
import { getExtendedProjectsQueryOptions } from "~/modules/projects/projects.queries"
import type { Pagination } from "~/modules/resources/components/resource-invoices-table"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "clients-projects-route",
})

const searchSchema = dataTableBaseSearchSchema

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/clients/$clientSlug/projects",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) =>
    pick(search, ["q", "page", "page_size", "sorting", "columnFilters"]),
  loader: async ({ context, params, deps }) => {
    context.queryClient
      .ensureQueryData(
        getExtendedClientQueryOptions({ clientSlug: params.clientSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended client", { error })
        return null
      })

    const filters = pick(deps, [
      "q",
      "page",
      "page_size",
      "sorting",
      "columnFilters",
    ])

    return {
      filters,
    }
  },
})

function RouteComponent() {
  const params = useParams({ from: Route.id })
  const { filters } = Route.useLoaderData()
  const { isProjectCreationOn } = useFeatureConfigs()
  const router = useRouter()
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false)

  const { data: client } = useSuspenseQuery(
    getExtendedClientQueryOptions({ clientSlug: params.clientSlug }),
  )

  const { data: projectsData } = useQuery(
    getExtendedProjectsQueryOptions({
      filters: {
        ...filters,
        columnFilters: [
          ...(filters.columnFilters ?? []),
          // Filter projects to only show those belonging to this client
          ...(client?.unit_id
            ? [{ id: "client_unit_ids", value: [client.unit_id] }]
            : []),
        ],
      },
    }),
  )

  const onSelectProject = (project: ExtendedProject) => {
    router.navigate({
      to: "/projects/$projectId/overview",
      params: { projectId: project.id },
      search: true,
    })
  }

  const handleAddProject = useStableCallback(() => {
    setIsCreateProjectModalOpen(true)
  })

  const pagination: Pagination = {
    total: projectsData?.count ?? 0,
    page: filters.page,
    page_size: filters.page_size,
  }

  if (!client) {
    throw notFound()
  }

  return (
    <div className="h-full flex-1 overflow-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-semibold text-lg text-white">Projects</h1>
          <If condition={isProjectCreationOn}>
            <Button size="sm" variant="glass-darker" onClick={handleAddProject}>
              <Plus className="mr-2 h-4 w-4" />
              Add Project
            </Button>
          </If>
        </div>
        <If
          condition={projectsData}
          fallback={
            <div className="h-full flex-1 overflow-auto">
              <div className="flex h-64 items-center justify-center">
                <div className="text-white/70">Loading projects...</div>
              </div>
            </div>
          }
        >
          {(data) => (
            <ProjectsTable
              projects={data.projects}
              onSelectProject={onSelectProject}
              selectedClient={client}
              pagination={pagination}
              initialColumnFilters={filters.columnFilters}
              initialSorting={filters.sorting}
            />
          )}
        </If>
      </div>

      <If condition={isProjectCreationOn && client.id}>
        <CreateProjectModal
          open={isCreateProjectModalOpen}
          onOpenChange={setIsCreateProjectModalOpen}
          preselectedClientId={client.id}
        />
      </If>
    </div>
  )
}
